import { useMemo, useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRichAniContext, type RichContext } from "./useRichAniContext";
import { feToolRegistry, type FeToolSchema, type FeToolResult } from "@/services/agent";

/**
 * Context for the FE agent session
 */
export interface FeSessionContext {
  /** Unique session ID for this browser session */
  sessionId: string;
  /** Current app based on route (e.g., 'cash-flow', 'screener', 'general') */
  currentApp: string;
  /** Tool schemas available in current context (for sending to backend) */
  availableTools: FeToolSchema[];
  /** Tool IDs available in current context */
  availableToolIds: string[];
  /** Current app state from API context */
  appState: Record<string, unknown>;
  /** Rich context from useRichAniContext */
  richContext: RichContext;
  /** Execute a FE tool by ID */
  executeTool: (toolId: string, params: Record<string, unknown>) => Promise<FeToolResult>;
  /** Check if a tool is available */
  hasTool: (toolId: string) => boolean;
  /** Switch to a different session */
  switchSession: (sessionId: string) => void;
  /** Create a new session and switch to it */
  createNewSession: () => string;
}

/**
 * Determine the current app from the route pathname
 */
function inferAppFromRoute(pathname: string): string {
  const routeToApp: Record<string, string> = {
    "cash-flow": "cash-flow",
    screener: "screener",
    portfolio: "portfolio",
    portfolios: "portfolio",
    "report-builder": "report-builder",
    dashboard: "dashboard",
    documents: "documents",
    pods: "pods",
    pod: "pods",
    workers: "workers",
    worker: "workers",
    playground: "playground",
    "scenario-tracker": "scenario-tracker",
  };

  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0] || "";

  return routeToApp[firstSegment] || "general";
}

/**
 * Generate a stable session ID for this browser session.
 * Persisted in sessionStorage so it survives page refreshes but not new tabs.
 */
export function getOrCreateSessionId(): string {
  const storageKey = "ani_fe_session_id";
  let sessionId = sessionStorage.getItem(storageKey);

  if (!sessionId) {
    sessionId = `fe_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }

  return sessionId;
}

/**
 * Hook to manage the FE agent session context.
 *
 * Provides:
 * - Session ID tracking
 * - Current app detection
 * - Available FE tools for current context
 * - Tool execution capability
 * - Rich context from useRichAniContext
 *
 * @example
 * const { currentApp, availableTools, executeTool, richContext } = useFeAgentSession();
 *
 * // Execute a tool
 * const result = await executeTool('cashflow_add_life_event', {
 *   name: 'Retirement',
 *   period: 15
 * });
 */
export function useFeAgentSession(): FeSessionContext {
  const location = useLocation();
  const richContext = useRichAniContext();
  const [sessionId, setSessionId] = useState<string>(() => getOrCreateSessionId());

  // Determine current app from route
  const currentApp = useMemo(() => {
    return inferAppFromRoute(location.pathname);
  }, [location.pathname]);

  // Get tool schemas for current app
  const availableTools = useMemo(() => {
    return feToolRegistry.getToolSchemasForApp(currentApp);
  }, [currentApp]);

  // Get tool IDs for current app
  const availableToolIds = useMemo(() => {
    return availableTools.map((t) => t.id);
  }, [availableTools]);

  // Extract app state from rich context
  const appState = useMemo(() => {
    return (richContext.apiContext?.pageData as Record<string, unknown>) || {};
  }, [richContext.apiContext?.pageData]);

  // Execute a tool
  const executeTool = useCallback(
    async (toolId: string, params: Record<string, unknown>): Promise<FeToolResult> => {
      console.log(`[FeAgentSession] Executing tool: ${toolId}`, params);
      const result = await feToolRegistry.execute(toolId, params);
      console.log(`[FeAgentSession] Tool result:`, result);
      return result;
    },
    []
  );

  // Check if a tool is available
  const hasTool = useCallback(
    (toolId: string): boolean => {
      return availableToolIds.includes(toolId);
    },
    [availableToolIds]
  );

  // Switch to a different session
  const switchSession = useCallback((newSessionId: string) => {
    setSessionId(newSessionId);
    sessionStorage.setItem("ani_fe_session_id", newSessionId);
  }, []);

  // Create a new session and switch to it
  const createNewSession = useCallback(() => {
    const newSessionId = `fe_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    setSessionId(newSessionId);
    sessionStorage.setItem("ani_fe_session_id", newSessionId);
    return newSessionId;
  }, []);

  return {
    sessionId,
    currentApp,
    availableTools,
    availableToolIds,
    appState,
    richContext,
    executeTool,
    hasTool,
    switchSession,
    createNewSession,
  };
}

/**
 * Hook to track tool registration changes.
 * Useful for debugging or displaying available tools in UI.
 */
export function useAvailableFeTools(appId?: string): FeToolSchema[] {
  const location = useLocation();

  const currentApp = useMemo(() => {
    return appId || inferAppFromRoute(location.pathname);
  }, [appId, location.pathname]);

  return useMemo(() => {
    return feToolRegistry.getToolSchemasForApp(currentApp);
  }, [currentApp]);
}
